<template>
  <div class="network-connection-guide">
    <div class="network-connection-guide__title">
      <div>{{ $t('Current status') }}:</div>
      <div class="network-connection-guide__close" @click="$emit('close')">
        <button class="outline-icon">
          <aha-icon
            name="close"
            height="14"
            width="14"
            viewBox="0 0 24 24"
          />
        </button>
      </div>
    </div>
    <div
      class="network-connection-guide__current"
      :class="{
        'status-good': isConnectionGood,
        'status-unstable': isConnectionUnstable,
        'status-error': isConnectionError,
      }"
    >
      <div v-if="isConnectionGood">
        <aha-icon
          name="connection-good"
          class="status-good"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        />
        <span v-html="$t('Excellent: Everything works smoothly.')" />
      </div>
      <div v-if="isConnectionUnstable">
        <aha-icon
          name="connection-unstable"
          class="status-unstable"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        />
        <span v-html="$t('Unstable: Some features may not work.')" />
      </div>
      <div v-if="isConnectionError">
        <aha-icon
          name="connection-error"
          class="status-error"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        />
        <span>
          <b>{{ $t('Error') }}: </b>
          <a
            name="link-contact-us"
            target="_blank"
            href="https://ahaslides.com/contact-us/"
          >
            <span class="underline mr-1">{{ $t('Contact us') }}</span>
            <aha-icon
              class="text-secondary-40"
              to="link-contact-us"
              name="open-new-window"
              width="16"
              height="16"
              viewBox="0 0 28 28"
            />
          </a> {{ $t('for support.') }}
        </span>
      </div>
    </div>
    <div class="network-connection-guide__other" v-show="showMore">
      <div class="pb-3">
        {{ $t('Other possible states') }}:
      </div>
      <div class="network-connection-guide__list">
        <div class="network-connection-guide__status" v-if="!isConnectionGood">
          <aha-icon
            name="connection-good"
            class="status-good"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
          <span v-html="$t('Excellent: Everything works smoothly.')" />
        </div>
        <div class="network-connection-guide__status" v-if="!isConnectionUnstable">
          <aha-icon
            name="connection-unstable"
            class="status-unstable"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
          <span v-html="$t('Unstable: Some features may not work.')" />
        </div>
        <div class="network-connection-guide__status" v-if="!isConnectionError">
          <aha-icon
            name="connection-error"
            class="status-error"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
          <span>
            <b>{{ $t('Error') }}: </b>
            <a
              name="link-contact-us"
              target="_blank"
              href="https://ahaslides.com/contact-us/"
            >
              <span class="underline mr-1">{{ $t('Contact us') }}</span>
              <aha-icon
                class="text-secondary-40"
                to="link-contact-us"
                name="open-new-window"
                width="16"
                height="16"
                viewBox="0 0 28 28"
              />
            </a> {{ $t('for support.') }}
          </span>
        </div>
      </div>
    </div>
    <div v-show="!showMore">
      <span class="underline cursor-pointer learn-more" @click="toggleMore">{{ $t('Learn more...') }}</span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { CONNECTION_STATUS } from '@/utils/system/SystemConnection'

export default {
  data() {
    return {
      showMore: false,
    }
  },
  computed: {
    ...mapGetters('systemConnection', ['connectionStatus']),

    isConnectionError() {
      return this.connectionStatus === CONNECTION_STATUS.ERROR
    },
    isConnectionGood() {
      return this.connectionStatus === CONNECTION_STATUS.GOOD
    },
    isConnectionUnstable() {
      return this.connectionStatus === CONNECTION_STATUS.UNSTABLE
    },
  },
  methods: {
    toggleMore() {
      this.showMore = !this.showMore
    },
  },
}
</script>

<style lang="css" scoped>
.network-connection-guide {
  @apply flex flex-col space-y-4 p-4 bg-secondary-100 absolute right-0;
  @apply bottom-12 border border-secondary-50 rounded-xl;
  color: #fff;
  font-size: 14px;
  min-width: 399px;
  width: max-content;

  .network-connection-guide__status {
    @apply relative flex items-center space-x-2;

    a {
      @apply underline;
      color: #fff;
    }
  }
  .network-connection-guide__title {
    @apply flex justify-between;

    svg {
      &:hover {
        @apply text-primary-90;
      }
      &:focus {
        @apply text-primary-110;
      }
      &:active {
        @apply text-primary-110;
      }
    }
  }
  .network-connection-guide__current {
    @apply p-3 border rounded-lg;

    &.status-good {
      @apply border-green-50;
    }
    &.status-unstable {
      @apply border-yellow-50;
    }
    &.status-error {
      @apply border-red-50;
    }
    > div {
      @apply flex items-center space-x-2;
    }
  }
  .network-connection-guide__list {
    @apply space-y-2;
  }
  .network-connection-guide__close {

    button {
      width: 21px;
      height: 21px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
.status-unstable {
  &::v-deep path[fill="#FFA826"] {
    fill: #FFA826;
  }
  &::v-deep path[fill="#282E3E"] {
    fill: #282E3E;
  }
}
.status-error {
  &::v-deep path[fill="#FF4954"] {
    fill: #FF4954;
  }
}
.status-good {
  &::v-deep path[fill="#33C173"] {
    fill: #33C173;
  }
  &::v-deep path[fill="#fff"] {
    fill: #fff;
  }
}
.outline-icon {
  @apply rounded-full cursor-pointer;
  background-color: transparent;

  &:hover {
    @apply text-primary-90;
  }
  &:active {
    @apply text-primary-110;
  }
  &:focus {
    outline: unset;
  }
  &:focus-visible {
    outline: 2px solid #89BEFA;
  }
}
.learn-more {
  &:hover {
    @apply text-primary-90;
  }
  &:active {
    @apply text-primary-110;
  }
}
</style>
